import React, { useState, useEffect } from 'react';
import { getCompanies, createCompany, updateCompany, deleteCompany } from '../api/api';
import {
    Box, Typography, Paper, Button, TextField, Alert, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, Stack, Chip,
    IconButton, Grid, FormControl, InputLabel, Select, MenuItem, Divider
} from '@mui/material';
import {
    Business as BusinessIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon
} from '@mui/icons-material';

const CompaniesPage = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        cnpj: '',
        email: '',
        phone: '',
        address: '',
        ai_provider: 'ollama',
        ai_config: {}
    });

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        setLoading(true);
        try {
            const response = await getCompanies();
            setCompanies(response.data);
            setError(null);
        } catch (err) {
            setError('Erro ao carregar empresas');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCompany) {
                await updateCompany(editingCompany.id, formData);
            } else {
                await createCompany(formData);
            }
            fetchCompanies();
            handleCloseModal();
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao salvar empresa');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja desativar esta empresa?')) {
            try {
                await deleteCompany(id);
                fetchCompanies();
            } catch (err) {
                setError(err.response?.data?.message || 'Erro ao desativar empresa');
            }
        }
    };

    const handleEdit = (company) => {
        setEditingCompany(company);
        setFormData({
            name: company.name,
            cnpj: company.cnpj || '',
            email: company.email || '',
            phone: company.phone || '',
            address: company.address || '',
            ai_provider: company.ai_provider || 'ollama',
            ai_config: company.ai_config || {}
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingCompany(null);
        setFormData({
            name: '',
            cnpj: '',
            email: '',
            phone: '',
            address: '',
            ai_provider: 'ollama',
            ai_config: {}
        });
        setError(null);
    };

    const formatCNPJ = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 14) {
            return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        return value;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'cnpj') {
            setFormData({ ...formData, [name]: formatCNPJ(value) });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleAIConfigChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            ai_config: {
                ...formData.ai_config,
                [name]: value
            }
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Typography variant="h4" fontWeight="bold">
                        Gerenciamento de Empresas
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setShowModal(true)}
                >
                    Nova Empresa
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>CNPJ</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Telefone</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {companies.map(company => (
                                    <TableRow key={company.id} hover>
                                        <TableCell>
                                            <Typography fontWeight="bold">{company.name}</Typography>
                                        </TableCell>
                                        <TableCell>{company.cnpj || '-'}</TableCell>
                                        <TableCell>{company.email || '-'}</TableCell>
                                        <TableCell>{company.phone || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={company.active ? <ActiveIcon /> : <InactiveIcon />}
                                                label={company.active ? 'Ativa' : 'Inativa'}
                                                color={company.active ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                onClick={() => handleEdit(company)}
                                                color="primary"
                                                size="small"
                                                sx={{ mr: 1 }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            {company.active && (
                                                <IconButton
                                                    onClick={() => handleDelete(company.id)}
                                                    color="error"
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {companies.length === 0 && (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography color="text.secondary">Nenhuma empresa cadastrada</Typography>
                        </Box>
                    )}
                </Paper>
            )}

            <Dialog open={showModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingCompany ? 'Editar Empresa' : 'Nova Empresa'}
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            <TextField
                                label="Nome da Empresa"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                fullWidth
                                required
                                placeholder="Ex: Hospital São Lucas"
                            />

                            <TextField
                                label="CNPJ"
                                name="cnpj"
                                value={formData.cnpj}
                                onChange={handleInputChange}
                                fullWidth
                                placeholder="00.000.000/0000-00"
                                inputProps={{ maxLength: 18 }}
                            />

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        fullWidth
                                        placeholder="contato@empresa.com"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Telefone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        fullWidth
                                        placeholder="(00) 0000-0000"
                                    />
                                </Grid>
                            </Grid>

                            <TextField
                                label="Endereço"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Endereço completo"
                            />

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle1" fontWeight="bold">Configuração de IA</Typography>

                            <FormControl fullWidth>
                                <InputLabel>Provedor de IA</InputLabel>
                                <Select
                                    name="ai_provider"
                                    value={formData.ai_provider}
                                    label="Provedor de IA"
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="ollama">Ollama (Local)</MenuItem>
                                    <MenuItem value="gemini">Google Gemini (Cloud)</MenuItem>
                                </Select>
                            </FormControl>

                            {formData.ai_provider === 'gemini' && (
                                <TextField
                                    label="API Key do Gemini"
                                    name="api_key"
                                    value={formData.ai_config?.api_key || ''}
                                    onChange={handleAIConfigChange}
                                    fullWidth
                                    type="password"
                                    placeholder="Cole sua chave de API aqui"
                                    helperText="Necessário para usar o Gemini 2.0"
                                />
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5 }}>
                        <Button onClick={handleCloseModal} color="inherit">
                            Cancelar
                        </Button>
                        <Button type="submit" variant="contained" color="primary">
                            {editingCompany ? 'Atualizar' : 'Criar'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default CompaniesPage;
